import { Inventory2Outlined } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import { BarChart } from '@mui/x-charts';
import { generateClient } from 'aws-amplify/api';
import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../contexts/UserContext';
import { campaignLogsByCampaign, esQuery } from '../../graphql/queries';
import { fetchAllPages } from '../../tools/graphqlPaging';

function formatChartData(aggregations) {
  if (!aggregations?.contacts?.buckets) return [];

  const completedAttemptCounts = {};
  const retryAttemptCounts = {};

  aggregations.contacts.buckets.forEach((contactBucket) => {
    const attempts = contactBucket.doc_count || 0;
    const statusBuckets = contactBucket.status?.buckets || [];

    const hasCompleted = statusBuckets.some((b) => b.key?.toLowerCase() === 'completed');
    const hasRetry = statusBuckets.some((b) => b.key?.toLowerCase() === 'retry');

    // ✅ Combine retry+completed into completed when both exist
    if (hasCompleted && hasRetry) {
      completedAttemptCounts[attempts] = (completedAttemptCounts[attempts] || 0) + 1;
    } else if (hasCompleted) {
      completedAttemptCounts[attempts] = (completedAttemptCounts[attempts] || 0) + 1;
    } else if (hasRetry) {
      retryAttemptCounts[attempts] = (retryAttemptCounts[attempts] || 0) + 1;
    }
  });

  const allAttempts = new Set([
    ...Object.keys(completedAttemptCounts),
    ...Object.keys(retryAttemptCounts),
  ]);

  return Array.from(allAttempts)
    .map((attempts) => ({
      attempts: Number(attempts),
      completed: completedAttemptCounts[attempts] || 0,
      retry: retryAttemptCounts[attempts] || 0,
    }))
    .sort((a, b) => a.attempts - b.attempts);
}

const SegmentPenetrationBreakdown = (props) => {
  const client = generateClient();
  const userContext = useContext(UserContext);
  const [campaignId, setCampaignId] = useState();
  const [segmentId, setSegmentId] = useState();
  const [segmentName, setSegmentName] = useState();
  const [channel, setChannel] = useState();

  const [retries, setRetries] = useState();
  const [completed, setCompleted] = useState();
  const [labelValue, setLabelValue] = useState();

  async function getLastCampaignStartDate() {
    const campaignLogs = await fetchAllPages({
      client,
      query: campaignLogsByCampaign,
      resultKey: 'campaignLogsByCampaign',
      variables: {
        campaign: campaignId,
        type: { eq: "Load" }
      }
    });

    let lastStartDate;
    // Compare each log and get the last start date
    for (const log of campaignLogs) {
      if (!lastStartDate || new Date(log.createdAt) > new Date(lastStartDate)) {
        lastStartDate = log.createdAt;
      }
    }

    // If last start date is longer ago than 90 days, set it to 90 days ago
    if (lastStartDate && moment(lastStartDate).isBefore(moment().subtract(90, 'days'))) {
      lastStartDate = moment().subtract(90, 'days').toISOString();
    }
    console.log("Last Campaign Start Date:", lastStartDate);
    return lastStartDate;
  }

  useEffect(() => {
    if (props.segmentId) {
      setCampaignId(props.campaignId);
      setSegmentId(props.segmentId);
      setChannel(props.channel);
    }
  }, [props.segmentId]);

  useEffect(() => {
    if (segmentId) getStatusBreakdown();
  }, [segmentId]);

  async function getStatusBreakdown() {
    try {
      setRetries(undefined);
      setCompleted(undefined);
      setLabelValue(undefined);

      const start = await getLastCampaignStartDate();
      const end = moment().toISOString();

      const query = buildQueryForTimeRange(
        userContext.tenantId,
        channel,
        campaignId,
        segmentId,
        start,
        end
      );

      const result = await client.graphql({
        query: esQuery,
        variables: {
          model: 'activitylog-*',
          action: '_search',
          query: JSON.stringify(query),
        },
      });

      const esQueryResult = JSON.parse(result.data?.esQuery);
      const chartData = formatChartData(esQueryResult.aggregations);

      setRetries(chartData.map((d) => d.retry));
      setCompleted(chartData.map((d) => d.completed));
      setLabelValue(chartData.map((d) => d.attempts));
      setSegmentName(props.segmentName);
    } catch (err) {
      console.error('Error fetching breakdown:', err);
    }
  }

  function buildQueryForTimeRange(
    tenantId,
    channel,
    campaignId,
    segmentId,
    start,
    end
  ) {
    return {
      aggs: {
        contacts: {
          terms: {
            field: 'activityLogContactId.keyword',
            size: 2147483647,
          },
          aggs: {
            status: {
              terms: {
                field: 'contactStatus.keyword',
                size: 2147483647,
              },
            },
          },
        },
      },
      query: {
        bool: {
          must: [
            { term: { 'tenant.keyword': tenantId } },
            { match_phrase: { type: 'Dial' } },
            { match_phrase: { activityLogCampaignId: campaignId } },
            { match_phrase: { activityLogSegmentId: segmentId } },
            { range: { createdAt: { gte: start, lte: end } } },
          ],
          must_not: [
            {
              match_phrase: {
                disposition: 'Cadence Exhaustion - Retry Increment',
              },
            },
          ],
        },
      },
      size: 0,
    };
  }

  async function closeDialog() {
    if (props.onCancel) props.onCancel();
    setRetries(undefined);
    setCompleted(undefined);
    setSegmentId(undefined);
  }

  return (
    <Box>
      <Card
        color="primary"
        elevation={0}
        variant="outlined"
        sx={{ borderRadius: '10px', height: '100%' }}
      >
        <CardHeader
          title={
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">
                {segmentName ? segmentName : 'Segment Inventory'}
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Tooltip title="View Campaign Inventory">
                  <IconButton
                    onClick={() => {
                      props.setBreakdownId();
                      props.setBreakdownName();
                      props.setShowSegmentPenetration(false);
                    }}
                    color="primary"
                  >
                    <Inventory2Outlined />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          }
        />
        <CardContent sx={{ height: '100%' }}>
          {retries && completed && labelValue ? (
            <BarChart
              series={[
                { data: retries, label: 'Retry', color: '#94a3b8', stack: '1' },
                { data: completed, label: 'Completed', color: '#3b82f6', stack: '1' },
              ]}
              height={240}
              borderRadius={6}
              xAxis={[
                { data: labelValue, scaleType: 'band', label: 'Attempts' },
              ]}
              yAxis={[{ label: 'Contacts' }]}
              margin={{ left: 70, right: 30, top: 20, bottom: 40 }}
            />
          ) : (
            <LinearProgress color="primary" />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SegmentPenetrationBreakdown;
